import { inject } from '@angular/core'
import { signalStoreFeature, type, withMethods, withState } from '@ngrx/signals'
import { Events, injectDispatch, on, withEventHandlers, withReducer } from '@ngrx/signals/events'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { EMPTY, filter, map, merge, of, pipe, race, switchMap, tap, throttle, timer } from 'rxjs'

import { UserChannelStore } from '@app/modules/action-cable/core/services/user-channel.store'

import { conversationEvents } from './conversation.events'
import type { ConversationState } from './conversation.store'
import { realtimeMessagingEvents } from './realtime-messaging.events'
import { sendingEvents } from './sending.events'
import type { Conversation } from '../../models/conversation.model'
import type { Message } from '../../models/message.model'

const INCOMING_TYPING_INDICATOR_TIMEOUT = 60_000
const SEND_TYPING_INDICATOR_TIMEOUT = 60_000

interface RealtimeMessagingFeatureState {
  otherProfileIsTyping: boolean
}

const initialState: RealtimeMessagingFeatureState = {
  otherProfileIsTyping: false,
}

export const realtimeMessagingFeature = () =>
  signalStoreFeature(
    { state: type<ConversationState>() },
    withState(initialState),
    withMethods((store, userChannelStore = inject(UserChannelStore), events = inject(Events)) => ({
      sendTyping: rxMethod<void>(
        pipe(
          throttle(() =>
            merge(
              events.on(conversationEvents.select),
              events.on(sendingEvents.sendMessageSuccess),
              timer(SEND_TYPING_INDICATOR_TIMEOUT),
            ),
          ),
          tap(() => userChannelStore.performTyping({ profile_browse_id: store.conversation()!.profile.browse_id })),
        ),
      ),
    })),
    withReducer(
      on(realtimeMessagingEvents.otherProfileStartedTyping, () => ({ otherProfileIsTyping: true })),
      on(realtimeMessagingEvents.otherProfileStoppedTyping, () => ({ otherProfileIsTyping: false })),
    ),
    withEventHandlers(
      (
        store,
        userChannelStore = inject(UserChannelStore),
        events = inject(Events),
        dispatch = injectDispatch(realtimeMessagingEvents),
      ) => ({
        messageUpsert$: userChannelStore.messages$.pipe(
          switchMap(message =>
            message.type === 'message_upsert' && store.conversation()?.profile.browse_id === message.profile_browse_id
              ? of(JSON.parse(message.message) as Message)
              : EMPTY,
          ),
          tap(message => dispatch.messageUpsert({ message })),
        ),

        conversationUpdate$: userChannelStore.messages$.pipe(
          switchMap(message =>
            message.type === 'conversation_update' &&
            store.conversation()?.profile.browse_id === message.profile_browse_id
              ? of(JSON.parse(message.conversation) as Partial<Conversation>)
              : EMPTY,
          ),
          tap(conversation => dispatch.conversationUpdate({ conversation })),
        ),

        otherProfileStartedTyping$: userChannelStore.messages$.pipe(
          switchMap(message =>
            message.type === 'conversation_typing' &&
            store.conversation()?.profile.browse_id === message.profile_browse_id
              ? of<void>(undefined)
              : EMPTY,
          ),
          tap(() => dispatch.otherProfileStartedTyping()),
        ),

        conversationReload$: merge(
          userChannelStore.messages$.pipe(
            switchMap(message =>
              message.type === 'conversation_reload' &&
              store.conversation()?.profile.browse_id === message.profile_browse_id
                ? of<void>(undefined)
                : EMPTY,
            ),
          ),
          userChannelStore.reconnect$.pipe(filter(() => !!store.conversation()?.profile.browse_id)),
        ).pipe(tap(() => dispatch.conversationReload())),

        markAsRead$: events.on(realtimeMessagingEvents.messageUpsert).pipe(
          map(({ payload }) => payload),
          filter(({ message }) => !message.is_me),
          tap(({ message }) =>
            userChannelStore.performMarkAsRead({
              message_timestamp: message.timestamp,
              profile_browse_id: store.conversation()!.profile.browse_id,
            }),
          ),
        ),

        stopTyping$: events.on(realtimeMessagingEvents.otherProfileStartedTyping).pipe(
          switchMap(() =>
            race(
              events.on(conversationEvents.select),
              events.on(realtimeMessagingEvents.messageUpsert).pipe(
                map(({ payload }) => payload),
                filter(({ message }) => !message.is_me),
              ),
              timer(INCOMING_TYPING_INDICATOR_TIMEOUT),
            ),
          ),
          tap(() => dispatch.otherProfileStoppedTyping()),
        ),
      }),
    ),
  )
